import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import uniqid from 'uniqid';

import Button from 'components/partials/button/button';

import ActionMenuItem from './action-menu-btn/action-menu-btn';

import Icons from 'assets/icons';

interface ActionMenuOption {
  label: string;
  icon?: React.ReactNode;
  isDelete?: boolean;
  onClick?: () => void;
  disabled?: boolean;
}

interface ActionMenuProps {
  buttonName?: string;
  options?: ActionMenuOption[];
  onEditClick?: () => void;
  isActionMenuDisabled?: boolean;
  iconBefore?: React.ReactNode;
  loading?: boolean;
}

const ActionMenu = ({
  buttonName = 'Edit profile',
  options = [],
  onEditClick,
  isActionMenuDisabled,
  iconBefore = <Icons.Edit aria-hidden="true" />,
  loading = false,
}: ActionMenuProps): React.JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const actionMenuBtn = useRef<HTMLButtonElement>(null);

  const handleClickOutside = (e: MouseEvent): void => {
    const target = e.target as Node;

    if (
      !actionMenuBtn.current?.contains(target) &&
      !dropdownRef.current?.contains(target)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="h-[44px] relative shrink-0">
      <div
        className={classNames('flex h-full', {
          'justify-end': !options.length,
        })}
      >
        <Button
          className={classNames(
            'action-button text-navy-800 rounded-r-none !px-10',
            {
              '!rounded-r-md': !options.length,
            }
          )}
          onClick={onEditClick}
          disabled={isActionMenuDisabled}
          iconBefore={iconBefore}
          loading={loading}
          variant="reserve-outlined"
          data-testid={`action-menu-button-${buttonName
            .toLowerCase()
            .replace(' ', '-')}`}
        >
          {buttonName}
        </Button>
        {!!options.length && (
          <Button
            ref={actionMenuBtn}
            className={classNames(
              'action-menu-item w-[44px] ml-1.5 rounded-l-none',
              {
                'bg-navy-50': isOpen,
              }
            )}
            onClick={() => {
              setIsOpen(!isOpen);
            }}
            variant="reserve-outlined"
            disabled={isActionMenuDisabled}
            aria-label={`Toggle ${buttonName} menu`}
            data-testid={`action-menu-dropdown-button-${buttonName
              .toLowerCase()
              .replace(' ', '-')}`}
          >
            <Icons.Arrow
              customClass={classNames(
                'arrow-button transition shrink-0 rotate-90',
                {
                  '!-rotate-90': isOpen,
                }
              )}
            />
          </Button>
        )}
      </div>
      {isOpen && (
        <div
          className="dropdown-menu w-full absolute z-[700] p-2 border border-navy-300 bg-white rounded-lg mt-1 shadow-lg text-left"
          ref={dropdownRef}
        >
          {options.map((option) => (
            <ActionMenuItem
              key={uniqid()}
              hideDropdownCallback={() => setIsOpen(false)}
              {...option}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ActionMenu;
